import React, { useState, useEffect } from 'react';
import { TourStep, TooltipPosition } from './types';
import TourProgress from './TourProgress';
import { Button } from '../Buttons';

interface TourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  targetElement: HTMLElement;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onComplete: () => void;
  onStepClick: (stepIndex: number) => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isLastStep: boolean;
}

const TourTooltip: React.FC<TourTooltipProps> = ({
  step,
  currentStep,
  totalSteps,
  targetElement,
  onNext,
  onPrevious,
  onSkip,
  onComplete,
  onStepClick,
  canGoNext,
  canGoPrevious,
  isLastStep,
}) => {
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    placement: 'auto' as const,
    arrowPosition: {},
  });

  useEffect(() => {
    if (!targetElement) return;

    const calculatePosition = (): TooltipPosition => {
      const targetRect = targetElement.getBoundingClientRect();
      const tooltipWidth = 360;
      const tooltipHeight = 200; // Estimated height
      const arrowSize = 8;
      const spotlightPadding = step.spotlightPadding || 8;
      const spacing = 16 + spotlightPadding; // Add spotlight padding to spacing

      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const centerX = targetRect.left + targetRect.width / 2;
      const centerY = targetRect.top + targetRect.height / 2;

      // Determine the best placement
      let placement = step.placement || 'auto';

      if (placement === 'auto') {
        // Auto-determine best placement based on available space
        const spaceAbove = targetRect.top;
        const spaceBelow = viewport.height - targetRect.bottom;
        const spaceLeft = targetRect.left;
        const spaceRight = viewport.width - targetRect.right;

        if (spaceBelow >= tooltipHeight + spacing) {
          placement = 'bottom';
        } else if (spaceAbove >= tooltipHeight + spacing) {
          placement = 'top';
        } else if (spaceRight >= tooltipWidth + spacing) {
          placement = 'right';
        } else if (spaceLeft >= tooltipWidth + spacing) {
          placement = 'left';
        } else {
          placement = 'bottom'; // Fallback
        }
      }

      let tooltipTop = 0;
      let tooltipLeft = 0;
      const arrowPosition: TooltipPosition['arrowPosition'] = {};

      switch (placement) {
        case 'top':
          tooltipTop = targetRect.top - tooltipHeight - spacing;
          tooltipLeft = Math.max(
            16,
            Math.min(
              centerX - tooltipWidth / 2,
              viewport.width - tooltipWidth - 16
            )
          );
          arrowPosition.bottom = -arrowSize;
          arrowPosition.left = Math.max(
            arrowSize,
            Math.min(centerX - tooltipLeft, tooltipWidth - arrowSize)
          );
          break;

        case 'bottom':
          tooltipTop = targetRect.bottom + spacing;
          tooltipLeft = Math.max(
            16,
            Math.min(
              centerX - tooltipWidth / 2,
              viewport.width - tooltipWidth - 16
            )
          );
          arrowPosition.top = -arrowSize;
          arrowPosition.left = Math.max(
            arrowSize,
            Math.min(centerX - tooltipLeft, tooltipWidth - arrowSize)
          );
          break;

        case 'left':
          tooltipTop = Math.max(
            16,
            Math.min(
              centerY - tooltipHeight / 2,
              viewport.height - tooltipHeight - 16
            )
          );
          tooltipLeft = targetRect.left - tooltipWidth - spacing;
          arrowPosition.right = -arrowSize;
          arrowPosition.top = Math.max(
            arrowSize,
            Math.min(centerY - tooltipTop, tooltipHeight - arrowSize)
          );
          break;

        case 'right':
          tooltipTop = Math.max(
            16,
            Math.min(
              centerY - tooltipHeight / 2,
              viewport.height - tooltipHeight - 16
            )
          );
          tooltipLeft = targetRect.right + spacing;
          arrowPosition.left = -arrowSize;
          arrowPosition.top = Math.max(
            arrowSize,
            Math.min(centerY - tooltipTop, tooltipHeight - arrowSize)
          );
          break;
      }

      // Ensure tooltip stays within viewport
      tooltipTop = Math.max(16, Math.min(tooltipTop, viewport.height - tooltipHeight - 16));
      tooltipLeft = Math.max(16, Math.min(tooltipLeft, viewport.width - tooltipWidth - 16));

      return {
        top: tooltipTop,
        left: tooltipLeft,
        placement: placement as 'top' | 'bottom' | 'left' | 'right',
        arrowPosition,
      };
    };

    const updatePosition = () => {
      setPosition(calculatePosition());
    };

    updatePosition();

    const handleResize = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [targetElement, step.placement, step.spotlightPadding]);

  const handleAction = () => {
    if (step.action) {
      step.action.onClick();
      // Check if action should auto-advance (can be configured per action)
      if (step.action.autoAdvance !== false) {
        // Wait for modal or element to appear before advancing
        setTimeout(() => {
          onNext();
        }, 600);
      }
    }
  };

  return (
    <div
      className="tour-tooltip"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      {/* Arrow */}
      <div
        className={`tour-arrow tour-arrow-${position.placement}`}
        style={position.arrowPosition}
      />

      {/* Content */}
      <div className="tour-tooltip-content">
        <div className="tour-tooltip-header">
          <h3 className="tour-tooltip-title">{step.title}</h3>
          <Button
            type="text"
            onClick={onSkip}
            aria-label="Close tour"
          >
            ✕
          </Button>
        </div>

        <p className="tour-tooltip-content-text">{step.content}</p>

        {/* Image (if provided) */}
        {step.image && (
          <div className="tour-tooltip-image mx-auto bg-gray-200">
            <img className='max-w-[190px]' src={step.image} alt={step.title} />
          </div>
        )}

        {/* Progress indicator */}
        <TourProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepClick={onStepClick}
        />

        {/* Action button (if provided) */}
        {step.action && (
          <div className="tour-action">
            <Button
              type="primary"
              onClick={handleAction}
            >
              {step.action.label}
            </Button>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="tour-tooltip-footer">
          <div className="tour-nav-buttons">
            {canGoPrevious && (
              <Button
                type="default"
                onClick={onPrevious}
              >
                Previous
              </Button>
            )}
            {!canGoPrevious && (
              <div style={{ visibility: 'hidden', width: '84px' }} />
            )}

            <Button
              type="text"
              onClick={onSkip}
            >
              Skip
            </Button>

            <Button
              type="primary"
              onClick={isLastStep ? onComplete : onNext}
            >
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </div>

          <div className="tour-step-counter">
            {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourTooltip;